// Payment runs: group supplier bills into one run, submit it, and read the
// remittance advice (table + PDF). Runs are persisted by the API and listed
// here. A draft stays Held until it is submitted. Amounts are whole currency
// units in the form, converted to integer minor units (cents) once, at
// create.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { BillPicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  type BatchRemittance,
  createPaymentBatch,
  downloadRemittancePdf,
  getBatchRemittance,
  listPaymentBatches,
  type PaymentBatchItemInput,
  type PaymentBatchRecord,
  submitPaymentBatch,
} from "@/lib/api";
import { useBillNumbers } from "@/lib/labels";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDay(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

/** Short tail of an opaque id for a fallback label — never the full UUID. */
function shortRef(id: string): string {
  return id.length > 10 ? `…${id.slice(-6)}` : id;
}

interface DraftItem {
  key: number;
  bill_id: string;
  supplier_name: string;
  amount: string;
}

interface SubmitTarget {
  id: string;
  name: string | null;
}

function batchStatus(batch: PaymentBatchRecord) {
  switch (batch.status) {
    case "completed":
      return <StatusDot tone="success" label="Completed" />;
    case "submitted":
      return <StatusDot tone="warning" label="Submitted — processing" />;
    default:
      return <StatusDot tone="idle" label="Held — not submitted" />;
  }
}

function BatchActions({
  batch,
  busy,
  onRemittance,
  onDownload,
  onSubmit,
}: {
  batch: PaymentBatchRecord;
  busy: boolean;
  onRemittance: () => void;
  onDownload: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={onRemittance}
      >
        Remittance
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={busy}
        onClick={onDownload}
      >
        PDF
      </Button>
      <Button
        type="button"
        size="sm"
        disabled={busy || batch.status !== "draft"}
        onClick={onSubmit}
      >
        {batch.status === "draft" ? "Submit" : "Submitted"}
      </Button>
    </div>
  );
}

export function PaymentBatchesPage() {
  const [name, setName] = useState("");
  const [items, setItems] = useState<DraftItem[]>([
    { key: 0, bill_id: "", supplier_name: "", amount: "" },
  ]);
  const [nextKey, setNextKey] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [batches, setBatches] = useState<PaymentBatchRecord[] | null>(null);
  const [batchesError, setBatchesError] = useState<string | null>(null);

  const reloadBatches = useCallback(() => {
    setBatchesError(null);
    listPaymentBatches()
      .then(setBatches)
      .catch((cause: unknown) => setBatchesError(toErrorMessage(cause)));
  }, []);

  useEffect(() => {
    reloadBatches();
  }, [reloadBatches]);

  const [asOf, setAsOf] = useState(todayInput);
  const [submitTarget, setSubmitTarget] = useState<SubmitTarget | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [lookupId, setLookupId] = useState("");
  const [remittanceId, setRemittanceId] = useState<string | null>(null);
  const [remittance, setRemittance] = useState<BatchRemittance | null>(null);
  const [remittanceLoading, setRemittanceLoading] = useState(false);
  const [remittanceError, setRemittanceError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState<{ run: () => void } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const billNumbers = useBillNumbers();

  const setItem = (key: number, patch: Partial<DraftItem>) => {
    setItems((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  };

  const openCreator = () => {
    setCreateError(null);
    setDrawerOpen(true);
  };

  const submitCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setCreateError("Give the batch a name first.");
      return;
    }
    const built: PaymentBatchItemInput[] = [];
    for (const row of items) {
      const minor = toMinor(row.amount);
      if (!row.bill_id || !row.supplier_name.trim() || minor === null) {
        setCreateError(
          "Every line needs a bill, a supplier name, and an amount above zero.",
        );
        return;
      }
      built.push({
        bill_id: row.bill_id,
        supplier_name: row.supplier_name.trim(),
        amount_cents: minor,
      });
    }
    if (built.length === 0) {
      setCreateError("Add at least one bill to the batch.");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createPaymentBatch({
        name: trimmedName,
        items: built,
      });
      if (!result?.id) {
        setCreateError(
          "The batch was created but the API returned no reference, so it cannot be listed here.",
        );
        return;
      }
      reloadBatches();
      setDrawerOpen(false);
      setName("");
      setItems([{ key: nextKey, bill_id: "", supplier_name: "", amount: "" }]);
      setNextKey((key) => key + 1);
      setNotice(
        `Created “${trimmedName}”. It is saved with this organization. Submit it when the bills are ready to record.`,
      );
    } catch (cause) {
      setCreateError(toErrorMessage(cause));
    } finally {
      setCreating(false);
    }
  };

  const loadRemittance = async (batchId: string) => {
    setRemittanceId(batchId);
    setRemittanceLoading(true);
    setRemittanceError(null);
    setRemittance(null);
    try {
      const result = await getBatchRemittance(batchId);
      if (!result || !Array.isArray(result.groups)) {
        setRemittanceError(
          "The remittance came back in an unexpected shape — nothing to show.",
        );
        return;
      }
      setRemittance(result);
    } catch (cause) {
      setRemittanceError(toErrorMessage(cause));
    } finally {
      setRemittanceLoading(false);
    }
  };

  const openSubmit = (target: SubmitTarget) => {
    setError(null);
    setRetry(null);
    setSubmitTarget(target);
  };

  const confirmSubmit = async () => {
    const target = submitTarget;
    if (!target || !asOf) return;
    setSubmitting(true);
    setError(null);
    setRetry(null);
    setNotice(null);
    try {
      const result = await submitPaymentBatch(target.id, { as_of: asOf });
      reloadBatches();
      setSubmitTarget(null);
      const count = result?.processed ?? 0;
      setNotice(
        target.name
          ? `Submitted “${target.name}” — ${count} payment${count === 1 ? "" : "s"} recorded as of ${asOf}. No money was sent to a bank.`
          : `Submitted the batch at ${target.id} — ${count} payment${count === 1 ? "" : "s"} recorded as of ${asOf}. No money was sent to a bank.`,
      );
      await loadRemittance(target.id);
    } catch (cause) {
      setSubmitTarget(null);
      setError(toErrorMessage(cause));
      setRetry({ run: () => openSubmit(target) });
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async (batchId: string) => {
    if (!batchId) {
      setError("Enter a batch reference first.");
      setRetry(null);
      return;
    }
    setDownloading(true);
    setError(null);
    setRetry(null);
    try {
      await downloadRemittancePdf(batchId);
    } catch (cause) {
      setError(toErrorMessage(cause));
      setRetry({ run: () => void downloadPdf(batchId) });
    } finally {
      setDownloading(false);
    }
  };

  const lookupRemittance = () => {
    const id = lookupId.trim();
    if (!id) {
      setError("Enter a batch reference first.");
      setRetry(null);
      return;
    }
    setError(null);
    setRetry(null);
    void loadRemittance(id);
  };

  const working = remittanceLoading || submitting;

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Payment runs"
        description="Group supplier bills into one run, submit it, then read the remittance advice. Submitting records the run and its journals; it never sends money to a bank."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={openCreator}>
              New batch
            </Button>
            <Button asChild variant="secondary">
              <Link
                to="/payments"
                search={(previous) => ({ ...previous, tab: "to-pay" })}
              >
                Back to payments
              </Link>
            </Button>
          </div>
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          {retry ? (
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={retry.run}
              >
                Retry
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
          {notice}
        </output>
      ) : null}

      <section aria-label="Payment runs" className="grid gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Payment runs</h2>
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">
            Draft runs stay held until submitted. Submitting records the
            payments and their journals; it never sends money to a bank.
          </p>
        </div>

        {batchesError ? (
          <div
            role="alert"
            className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
          >
            {batchesError}
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={reloadBatches}
              >
                Retry
              </Button>
            </div>
          </div>
        ) : batches === null ? (
          <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
        ) : batches.length === 0 ? (
          <EmptyState
            title="No payment runs yet"
            reason="Create a run to group supplier bills, then submit it to record the payments and their journals."
            action={
              <Button type="button" onClick={openCreator}>
                Create the first batch
              </Button>
            }
          />
        ) : (
          <>
            <div
              className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
              data-testid="payment-batches-table"
            >
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Batch</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead className="text-right">Lines</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {batches.map((batch) => (
                      <TableRow key={batch.id}>
                        <TableCell>
                          <span className="font-medium">{batch.name}</span>
                          <ul className="mt-1 grid gap-0.5 text-xs text-[var(--muted-foreground)]">
                            {batch.items.map((line) => (
                              <li key={line.id}>
                                {billNumbers.get(line.bill_id) ??
                                  `Bill ${shortRef(line.bill_id)}`}{" "}
                                — {line.supplier_name} —{" "}
                                {money(line.amount_cents)}
                              </li>
                            ))}
                          </ul>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {batch.id}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {batch.items.length}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {money(batch.total_amount_cents)}
                        </TableCell>
                        <TableCell>{batchStatus(batch)}</TableCell>
                        <TableCell className="text-right">
                          <BatchActions
                            batch={batch}
                            busy={working || downloading}
                            onRemittance={() =>
                              void loadRemittance(batch.id)
                            }
                            onDownload={() => void downloadPdf(batch.id)}
                            onSubmit={() =>
                              openSubmit({ id: batch.id, name: batch.name })
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="payment-batches-stack"
            >
              {batches.map((batch) => (
                <article
                  key={batch.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title">{batch.name}</span>
                    <span className="font-medium tabular-nums">
                      {money(batch.total_amount_cents)}
                    </span>
                  </div>
                  <CardField label="Reference">
                    <span className="font-mono text-xs">{batch.id}</span>
                  </CardField>
                  <CardField label="Supplier lines">
                    {batch.items
                      .map(
                        (line) =>
                          `${billNumbers.get(line.bill_id) ?? `Bill ${shortRef(line.bill_id)}`} — ${line.supplier_name} — ${money(line.amount_cents)}`,
                      )
                      .join(" · ")}
                  </CardField>
                  <CardField label="Status">{batchStatus(batch)}</CardField>
                  <div className="pt-3">
                    <BatchActions
                      batch={batch}
                      busy={working || downloading}
                      onRemittance={() => void loadRemittance(batch.id)}
                      onDownload={() => void downloadPdf(batch.id)}
                      onSubmit={() =>
                        openSubmit({ id: batch.id, name: batch.name })
                      }
                    />
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      <section aria-label="Remittance advice" className="grid gap-3">
        <h2 className="text-lg font-semibold tracking-tight">
          Remittance advice
        </h2>

        {remittanceLoading ? (
          <p className="text-sm text-[var(--muted-foreground)]">
            Loading remittance…
          </p>
        ) : remittanceError ? (
          <div
            role="alert"
            className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
          >
            {remittanceError}
            {remittanceId ? (
              <div className="mt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void loadRemittance(remittanceId)}
                >
                  Retry
                </Button>
              </div>
            ) : null}
          </div>
        ) : remittance ? (
          <div className="grid gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-[var(--muted-foreground)]">
                {remittance.batch_name} · {remittance.groups.length} supplier
                {remittance.groups.length === 1 ? "" : "s"} · total{" "}
                {money(remittance.grand_total_paid_cents)}.
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={downloading}
                onClick={() => void downloadPdf(remittance.batch_id)}
              >
                {downloading ? "Downloading…" : "Download PDF"}
              </Button>
            </div>
            {remittance.groups.map((group) => (
              <section
                key={group.supplier_name}
                aria-label={group.supplier_name}
                className="grid gap-2"
              >
                <h3 className="surface rounded-xl bg-[var(--muted)] px-5 py-2 text-xs font-semibold tracking-widest uppercase text-[var(--muted-foreground)]">
                  {group.supplier_name} · {money(group.total_paid_cents)}
                </h3>
                <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
                  <div className="table-scroll">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Bill</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">
                            Bill total
                          </TableHead>
                          <TableHead className="text-right">Paid</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.lines.map((line) => (
                          <TableRow key={line.bill_id}>
                            <TableCell>
                              {billNumbers.get(line.bill_id) ??
                                line.bill_number}
                            </TableCell>
                            <TableCell>{formatDay(line.bill_date)}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {money(line.bill_total_cents)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {money(line.amount_paid_cents)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <div className="stack-list grid gap-3 min-[64rem]:hidden">
                  {group.lines.map((line) => (
                    <article
                      key={line.bill_id}
                      className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                    >
                      <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                        <span className="record-title">
                          {billNumbers.get(line.bill_id) ?? line.bill_number}
                        </span>
                        <span className="font-medium tabular-nums">
                          {money(line.amount_paid_cents)} paid
                        </span>
                      </div>
                      <CardField label="Bill date">
                        {formatDay(line.bill_date)}
                      </CardField>
                      <CardField label="Bill total">
                        {money(line.bill_total_cents)}
                      </CardField>
                      <CardField label="Paid">
                        {money(line.amount_paid_cents)}
                      </CardField>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <EmptyState
            title="No remittance loaded"
            reason="Pick a batch above to submit it and read what each supplier was paid, or look up an existing reference under Advanced."
          />
        )}

        <details className="surface rounded-2xl p-5">
          <summary className="cursor-pointer text-sm font-semibold">
            Look up an existing reference (advanced)
          </summary>
          <p className="mt-2 text-xs text-[var(--muted-foreground)]">
            This is for a run reference created elsewhere. Saved runs are
            listed above, so you only need this form for a reference from
            another system (usually a UUID).
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_auto_auto] sm:items-end">
            <label
              htmlFor="pb-lookup"
              className="grid gap-1 text-xs font-medium"
            >
              Batch reference
              <Input
                id="pb-lookup"
                value={lookupId}
                onChange={(event) => setLookupId(event.target.value)}
                placeholder="e.g. 0f8b1c2d-4e5f-…"
                autoComplete="off"
                disabled={working}
              />
            </label>
            <Button
              type="button"
              variant="secondary"
              disabled={working || !lookupId.trim()}
              onClick={lookupRemittance}
            >
              {remittanceLoading ? "Looking up…" : "Show remittance"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={downloading || !lookupId.trim()}
              onClick={() => void downloadPdf(lookupId.trim())}
            >
              {downloading ? "Downloading…" : "Remittance (PDF)"}
            </Button>
          </div>
          <div className="mt-3">
            <Button
              type="button"
              disabled={working || !lookupId.trim()}
              onClick={() =>
                openSubmit({ id: lookupId.trim(), name: null })
              }
            >
              Submit this reference…
            </Button>
          </div>
        </details>
      </section>

      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title="New payment run"
        description="Group supplier bills into one batch. Creating a batch only records the plan; you submit it separately, and submitting never sends money to a bank."
        className="w-[min(34rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDrawerOpen(false)}
              disabled={creating}
            >
              Back to batches
            </Button>
            <Button
              type="submit"
              form="pb-create-form"
              disabled={creating}
              className="ms-auto"
            >
              {creating ? "Creating…" : "Create batch"}
            </Button>
          </>
        }
      >
        <form
          id="pb-create-form"
          onSubmit={submitCreate}
          className="grid gap-4"
          aria-label="Create a payment run"
        >
          {createError ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {createError}
            </div>
          ) : null}
          <label
            htmlFor="pb-name"
            className="grid gap-1 text-xs font-medium"
          >
            Batch name
            <Input
              id="pb-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="September supplier run"
              disabled={creating}
            />
          </label>
          <div className="grid gap-2">
            {items.map((row, index) => (
              <fieldset
                key={row.key}
                className="grid gap-2 rounded-xl border border-[var(--border)] p-3 sm:grid-cols-4"
              >
                <legend className="px-1 text-xs font-medium text-[var(--muted-foreground)]">
                  Line {index + 1}
                </legend>
                <label
                  className="grid gap-1 text-xs font-medium"
                  htmlFor={`pb-bill-${row.key}`}
                >
                  Bill
                  <BillPicker
                    id={`pb-bill-${row.key}`}
                    value={row.bill_id}
                    onChange={(bill_id) => setItem(row.key, { bill_id })}
                    disabled={creating}
                  />
                </label>
                <label
                  className="grid gap-1 text-xs font-medium"
                  htmlFor={`pb-supplier-${row.key}`}
                >
                  Supplier
                  <Input
                    id={`pb-supplier-${row.key}`}
                    value={row.supplier_name}
                    onChange={(event) =>
                      setItem(row.key, { supplier_name: event.target.value })
                    }
                    placeholder="Acme Supplies"
                    disabled={creating}
                  />
                </label>
                <label
                  className="grid gap-1 text-xs font-medium"
                  htmlFor={`pb-amount-${row.key}`}
                >
                  Amount ({DISPLAY_CURRENCY})
                  <Input
                    id={`pb-amount-${row.key}`}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={row.amount}
                    onChange={(event) =>
                      setItem(row.key, { amount: event.target.value })
                    }
                    placeholder="0.00"
                    disabled={creating}
                  />
                </label>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={creating || items.length <= 1}
                    onClick={() =>
                      setItems((prev) =>
                        prev.filter((candidate) => candidate.key !== row.key),
                      )
                    }
                  >
                    Remove
                  </Button>
                </div>
              </fieldset>
            ))}
          </div>
          <div>
            <Button
              type="button"
              variant="secondary"
              disabled={creating}
              onClick={() => {
                setItems((prev) => [
                  ...prev,
                  { key: nextKey, bill_id: "", supplier_name: "", amount: "" },
                ]);
                setNextKey((key) => key + 1);
              }}
            >
              Add a bill
            </Button>
          </div>
        </form>
      </Drawer>

      <Dialog
        open={submitTarget !== null}
        onOpenChange={(open) => {
          if (!open && !submitting) setSubmitTarget(null);
        }}
        title={
          submitTarget?.name
            ? `Submit “${submitTarget.name}”?`
            : "Submit this batch?"
        }
        description="Submitting records the batch and its journals in the books as of the date below. It never sends money to a bank, and it cannot be undone from this page."
      >
        <div className="grid gap-4">
          <label htmlFor="pb-asof" className="grid gap-1 text-xs font-medium">
            Submit as of
            <Input
              id="pb-asof"
              type="date"
              value={asOf}
              onChange={(event) => setAsOf(event.target.value)}
              disabled={submitting}
            />
          </label>
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setSubmitTarget(null)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void confirmSubmit()}
              disabled={submitting || !asOf}
            >
              {submitting ? "Submitting…" : "Submit batch"}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
